/**
 * Resolving stored-file URLs to something the browser can actually fetch.
 *
 * The backend returns whatever its storage adapter produced. Local disk gives
 * a root-relative `/media/products/a3/a3f1....jpg`; S3 gives an absolute
 * `https://bucket.s3.../products/...`. A root-relative URL resolves against
 * whatever origin the page came from, which is the API only by coincidence -
 * in dev it is the Vite server, and in a split deployment it is the CDN. Both
 * answer with something that is not the image.
 *
 * So relative URLs are resolved against the API origin explicitly. In the
 * common same-origin setup this changes nothing and the value passes straight
 * through; it is the split-origin case it exists for.
 */
import { API_BASE_URL } from '@/lib/apiClient';

/** Already-fetchable forms: absolute, protocol-relative, or inline data. */
const ABSOLUTE = /^(https?:)?\/\/|^data:|^blob:/i;

/**
 * The origin serving uploads, or '' when it is the page's own.
 *
 * Derived from the API base rather than configured separately, because the two
 * are the same server in every deployment this supports. A second env var
 * would just be one more thing to get out of step.
 */
const MEDIA_ORIGIN = (() => {
  if (!API_BASE_URL || !ABSOLUTE.test(API_BASE_URL)) return '';
  try {
    return new URL(API_BASE_URL).origin;
  } catch {
    // A malformed VITE_API_BASE_URL should not blank out every image on the
    // site; falling back to same-origin at least works behind the proxy.
    return '';
  }
})();

/**
 * Turn a stored-file URL into one the browser can load.
 *
 * Returns null for a missing value so callers can branch on it directly and
 * render a placeholder, rather than emitting `<img src="">` - which browsers
 * resolve to the current page and re-request, quietly doubling page loads.
 *
 * @param {string | null | undefined} url
 * @returns {string | null}
 */
export function mediaUrl(url) {
  if (!url) return null;
  if (ABSOLUTE.test(url)) return url;
  if (!MEDIA_ORIGIN) return url;
  return `${MEDIA_ORIGIN}/${url.replace(/^\//, '')}`;
}
